import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { createClinic, updateClinic } from '../services/adminService';

const ClinicDialog = ({ open, onClose, clinic }) => {
  const [formData, setFormData] = useState({
    name: '',
    business_id: '',
    practitioner_ids: [],
    stub: '',
    logo_url: '',
    logo_url_secondary: '',
    primary_color: '#3f51b5',
    is_active: true,
    timezone: 'Australia/Sydney',
    display_mode: 'flat'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [practitionerInput, setPractitionerInput] = useState('');
  
  // Load clinic data into form when editing
  useEffect(() => {
    if (clinic) {
      setFormData({
        ...clinic,
        logo_url: clinic.logo_url || '',
        logo_url_secondary: clinic.logo_url_secondary || ''
      });
    } else {
      // Reset form for new clinic
      setFormData({
        name: '',
        business_id: '',
        practitioner_ids: [],
        stub: '',
        logo_url: '',
        logo_url_secondary: '',
        primary_color: '#3f51b5',
        is_active: true,
        timezone: 'Australia/Sydney',
        display_mode: 'flat'
      });
    }
  }, [clinic]);
  
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? checked : value
    }));
  };
  
  const handleStubChange = (e) => {
    // Allow only letters, numbers, hyphens, and underscores
    const value = e.target.value.replace(/[^a-zA-Z0-9-_]/g, '');
    setFormData(prev => ({
      ...prev,
      stub: value
    }));
  };
  
  const handleAddPractitioner = () => {
    if (practitionerInput.trim()) {
      setFormData(prev => ({
        ...prev,
        practitioner_ids: [...prev.practitioner_ids, practitionerInput.trim()]
      }));
      setPractitionerInput('');
    }
  };
  
  const handleDeletePractitioner = (index) => {
    setFormData(prev => ({
      ...prev,
      practitioner_ids: prev.practitioner_ids.filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmit = async () => {
    // Validate form
    if (!formData.name || !formData.business_id || formData.practitioner_ids.length === 0 || !formData.stub) {
      setError('Name, Business ID, at least one Practitioner ID, and URL Stub are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (clinic) {
        // Update existing clinic
        await updateClinic(clinic.id, formData);
      } else {
        // Create new clinic
        await createClinic(formData);
      }
      
      onClose(true); // Close with refresh needed
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save clinic');
      console.error('Error saving clinic:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {clinic ? `Edit Clinic: ${clinic.name}` : 'Add New Clinic'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Clinic Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="business_id"
              label="Clinico Business ID"
              value={formData.business_id}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              helperText="From your Clinico admin panel"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="stub"
              label="URL Stub"
              value={formData.stub}
              onChange={handleStubChange}
              fullWidth
              required
              disabled={loading}
              helperText={`Will be accessible at https://booking.hydralyfe.com.au/${formData.stub || '[clinic-stub]'}`}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Clinico Practitioner IDs *
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {formData.practitioner_ids.map((id, index) => (
                <Chip
                  key={index}
                  label={id}
                  onDelete={() => handleDeletePractitioner(index)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={practitionerInput}
                onChange={(e) => setPractitionerInput(e.target.value)}
                placeholder="Enter Practitioner ID"
                fullWidth
                disabled={loading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddPractitioner();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={handleAddPractitioner}
                disabled={!practitionerInput.trim() || loading}
              >
                Add
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              From your Clinico practitioner profiles. Add multiple if needed.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="primary_color"
              label="Primary Color"
              value={formData.primary_color}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              type="color"
              helperText="Brand color for the booking page"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="timezone-label">Timezone</InputLabel>
              <Select
                labelId="timezone-label"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
                label="Timezone"
                disabled={loading}
              >
                <MenuItem value="Australia/Sydney">Sydney/Melbourne (AEST/AEDT)</MenuItem>
                <MenuItem value="Australia/Adelaide">Adelaide (ACST/ACDT)</MenuItem>
                <MenuItem value="Australia/Brisbane">Brisbane (AEST)</MenuItem>
                <MenuItem value="Australia/Perth">Perth (AWST)</MenuItem>
                <MenuItem value="Australia/Darwin">Darwin (ACST)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="display-mode-label">Service Display Mode</InputLabel>
              <Select
                labelId="display-mode-label"
                name="display_mode"
                value={formData.display_mode}
                onChange={handleChange}
                label="Service Display Mode"
                disabled={loading}
              >
                <MenuItem value="flat">Flat List (Default)</MenuItem>
                <MenuItem value="grouped">Grouped by Category</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="logo_url"
              label="Primary Logo URL"
              value={formData.logo_url}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="URL to the clinic's primary logo image (optional)"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="logo_url_secondary"
              label="Secondary Logo URL"
              value={formData.logo_url_secondary}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="URL to a secondary logo to display next to the primary logo (optional)"
            />
          </Grid>


          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => onClose(false)} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {clinic ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClinicDialog;